import React, { useState, useMemo, useEffect, useCallback, useRef, memo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './OverallPerformanceGraph.css';
import './EventOverViewGraph.css';
import { useSelector } from 'react-redux';
import { left_click } from '../../assets/images/images';
import { Chart_data } from "../../assets/images/images";
import { Ring } from "@uiball/loaders";
import { isloading } from '../../state/reducers/notificationListReducer';

// Optimized CustomTooltip with minimal re-renders
const CustomTooltip = memo(({ active, payload, hoveredUniqueId }) => {
  if (!active || !payload || payload.length === 0 || !hoveredUniqueId) {
    return null;
  }

  const validPayload = payload.filter(item => item.payload?.type !== 'placeholder');
  if (validPayload.length === 0) return null;

  const exactMatch = validPayload.find(item => item.payload?.uniqueId === hoveredUniqueId);
  if (!exactMatch) return null;

  const data = exactMatch.payload;
  if (!data.date || !data.student_name || data.score === undefined) return null;
  
  const formattedDate = data.date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  });
  
  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
  };

  return (
    <div style={{
      backgroundColor: '#1a202c',
      padding: '0',
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
      fontSize: '14px',
      minWidth: '320px',
      color: 'white',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      pointerEvents: 'none'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
                        backgroundColor: data.type === 'Course Assignment' ? '#4A90E2' : 
                              data.type === 'Practice Assignment' ? '#FF8C42' : 
                              data.type === 'Test Prep' ? '#9B59B6' : '#9B59B6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '600',
            color: 'white',
            padding: "5px"
          }}>
            {getInitials(data.student_name)}
          </div>
          
          <div style={{ 
            fontWeight: '600', 
            fontSize: '14px',
            color: '#ffffff',
            borderRight: "2px solid #fff",
            padding: "5px 7px 5px 5px"
          }}>
            {data.student_name || 'Student Name'}
          </div>
            
          <div style={{ 
            color: '#ffffff', 
            fontSize: '14px',
            borderRight: "2px solid #fff",
            padding: "5px 7px"
          }}>
            {formattedDate}
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          color: '#ffffff',
          fontSize: '14px',
          padding: '5px 5px 5px 7px',
          borderRadius: '6px'
        }}>
          <span><img src={left_click} alt='left-click' style={{marginRight:"5px"}}/></span>
          Click to see AI Feedback
        </div>
      </div>
      
      <div style={{ 
        padding: '0 20px 16px 20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: data.type === 'Course Assignment' ? '#4A90E2' : 
                              data.type === 'Practice Assignment' ? '#FF8C42' : 
                              data.type === 'Test Prep' ? '#9B59B6' :
                              data.type === 'Test Prep Practice' ? '#E74C3C' : '#666',
            marginRight: '10px',
            flexShrink: 0
          }}></div>
          
          <span style={{ 
            fontSize: '15px', 
            fontWeight: '500',
            color: 'white',
            lineHeight: '1.4'
          }}>
            Scored {data.score}% in {data.assignment_name || data.course_name || 'Assignment'}
          </span>
        </div>
        

      </div>
    </div> 
  );
}, (prevProps, nextProps) => {
  return prevProps.hoveredUniqueId === nextProps.hoveredUniqueId && 
         prevProps.active === nextProps.active;
});

const EventOverViewGraph = ({setdot, selectedCourseData, selectedStudentData, periodTab, year, month, startdate, enddate, monthYear, isAIGenerating}) => {

  const data = useSelector((state) => state?.getEventOverviewByTeacher?.students?.data || []);
  const isLoading = useSelector((state) => state?.getEventOverviewByTeacher?.loading || false);

  // Minimal hover state - only affects tooltip
  const [hoveredUniqueId, setHoveredUniqueId] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split('-');
    return new Date(year, month - 1, day);
  };

  // Helper function to convert month number to month name (MMM format)
  const getMonthName = (monthNumber) => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const validMonth = parseInt(monthNumber, 10);
    if (isNaN(validMonth) || validMonth < 1 || validMonth > 12) {
      return "Jan";
    }
    return months[validMonth - 1];
  };

  const parseScore = (scoreString) => {
    // Security: use regex /g flag so ALL '%' chars are removed (not just first occurrence)
    return parseFloat(scoreString.replace(/%/g, ''));
  };

  const transformAPIDataToChartFormat = (apiData) => {
    const allTransformedData = [];

    if (apiData.assignment_data) {
      apiData.assignment_data.forEach(periodData => {
        if (periodData.analytics_data?.length > 0) {
          periodData.analytics_data.forEach(item => {
            allTransformedData.push({
              date: parseDate(item.submission_date),
              originalDate: item.submission_date,
              score: parseScore(item.score),
              type: 'Course Assignment',
              course_name: item.course_name,
              assignment_name: item.assignment_name,
              student_name: item.student_name,
              course_id: item.course_id,
              student_id: item.student_id
            });
          });
        }
      });
    }

    if (apiData.practice_assignment_data) {
      apiData.practice_assignment_data.forEach(periodData => {
        if (periodData.analytics_data?.length > 0) {
          periodData.analytics_data.forEach(item => {
            allTransformedData.push({
              date: parseDate(item.submission_date),
              originalDate: item.submission_date,
              score: parseScore(item.score),
              type: 'Practice Assignment',
              course_name: item.course_name,
              assignment_name: item.assignment_name,
              student_name: item.student_name,
              course_id: item.course_id,
              student_id: item.student_id
            });
          });
        }
      });
    }

    if (apiData.test_prep_data) {
      apiData.test_prep_data.forEach(periodData => {
        if (periodData.analytics_data?.length > 0) {
          periodData.analytics_data.forEach(item => {
            allTransformedData.push({
              date: parseDate(item.submission_date),
              originalDate: item.submission_date,
              score: parseScore(item.score),
              type: 'Test Prep',
              course_name: item.course_name,
              assignment_name: item.assignment_name,
              student_name: item.student_name,
              course_id: item.course_id,
              student_id: item.student_id
            });
          });
        }
      });
    }

    return allTransformedData;
  };

  const getDistributedDatesWithStartEnd = (startDate, endDate, totalTicks = 3) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = end.getTime() - start.getTime();
    
    if (timeDiff === 0) return [start];
    if (totalTicks === 2 || timeDiff <= 24 * 60 * 60 * 1000) return [start, end];
    
    const distributedDates = [start];
    for (let i = 1; i < totalTicks - 1; i++) {
      const position = i / (totalTicks - 1);
      distributedDates.push(new Date(start.getTime() + (timeDiff * position)));
    }
    distributedDates.push(end);
    
    return distributedDates;
  };

  const generateTimePeriodsAndLabels = (periodTab, year, month, startdate, enddate) => {
    if (periodTab === 'week') {
      const currentYear = year || new Date().getFullYear();
      const currentMonth = month || new Date().getMonth() + 1;
      const firstDay = new Date(currentYear, currentMonth - 1, 1);
      const lastDay = new Date(currentYear, currentMonth, 0);
      
      const days = [];
      const labels = [];
      const current = new Date(firstDay);
      
      while (current <= lastDay) {
        days.push(new Date(current));
        const dateStr = current.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        const dayStr = current.toLocaleDateString('en-US', { weekday: 'short' });
        labels.push(`${dateStr}\n${dayStr}`);
        current.setDate(current.getDate() + 1);
      }
      
      return { periods: days, labels, windowSize: 7 };
      
    } else if (periodTab === 'custom') {
      if (!startdate || !enddate || startdate === "" || enddate === "") {
        return { periods: [], labels: [], windowSize: 2, randomTicks: [], randomLabels: [] };
      }
      
      const days = [];
      const labels = [];
      const current = parseDate(startdate);
      const endDate = parseDate(enddate);

      current.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      while (current <= endDate) {
        days.push(new Date(current));
        labels.push(current.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
        current.setDate(current.getDate() + 1);
      }
    
      const totalDays = days.length;
      let tickCount = totalDays === 1 ? 1 : totalDays === 2 ? 2 : totalDays <= 7 ? 3 : totalDays <= 30 ? 4 : 5;
      
      const distributedDates = getDistributedDatesWithStartEnd(parseDate(startdate), parseDate(enddate), tickCount);
      const randomTicks = [];
      const randomLabels = [];
      
      distributedDates.forEach(distributedDate => {
        const position = days.findIndex(day => day.toDateString() === distributedDate.toDateString());
        if (position !== -1) {
          randomTicks.push(position);
          randomLabels.push(distributedDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
        }
      });
      
      return { periods: days, labels, windowSize: days.length, randomTicks, randomLabels };
      
    } else if (periodTab === 'month') {
      const currentYear = year || new Date().getFullYear();
      const currentMonth = month || new Date().getMonth() + 1;
      const lastDayOfMonth = getLastDayOfMonth(currentYear, currentMonth);
      
      const weeklyDates = [1, 8, 15, 22, lastDayOfMonth];
      const periods = [];
      const labels = [];
      
      weeklyDates.forEach(day => {
        const date = new Date(currentYear, currentMonth - 1, day);
        periods.push(date);
        labels.push(date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
      });
      
      return { periods, labels, windowSize: 5 };
      
    } else if (periodTab === 'year') {
      const currentYear = year || new Date().getFullYear();
      const months = [];
      const labels = [];
      
      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const date = new Date(currentYear, monthIndex, 1);
        months.push(date);
        labels.push(date.toLocaleDateString('en-US', { month: 'short' }));
      }
      
      return { periods: months, labels, windowSize: 12 };
    }
    
    return { periods: [], labels: [], windowSize: 6 };
  };

  // Main chart data calculation - isolated from hover state
  const chartData = useMemo(() => {
    const transformedData = data && Object.keys(data).length > 0 ? transformAPIDataToChartFormat(data) : [];
    const { periods, labels, windowSize, randomTicks, randomLabels } = generateTimePeriodsAndLabels(periodTab, year, month, startdate, enddate);

    const dataWithPositions = transformedData.map((item, index) => {
      const itemDate = item.date;
      const uniqueId = `${item.student_id}-${item.course_id}-${item.assignment_name}-${item.originalDate}-${index}`;
      
      let position = 0;
      
      if (periodTab === 'week' || periodTab === 'custom') {
        const dayPosition = periods.findIndex(period => period.toDateString() === itemDate.toDateString());
        if (dayPosition !== -1) {
          position = dayPosition + (Math.random() - 0.5) * 0.3 + (index % 10) * 0.02;
        } else {
          return null;
        }
      } else if (periodTab === 'month') {
        const currentYear = year || new Date().getFullYear();
        const currentMonth = month || new Date().getMonth() + 1;
        const lastDayOfMonth = getLastDayOfMonth(currentYear, currentMonth);
        const day = itemDate.getDate();
        
        // Fix: Correctly assign week indices (0-4 for 5 weeks)
        let weekIndex;
        if (day >= 1 && day < 8) {
          weekIndex = 0;
        } else if (day >= 8 && day < 15) {
          weekIndex = 1;
        } else if (day >= 15 && day < 22) {
          weekIndex = 2;
        } else if (day >= 22) {
          weekIndex = 3; // Fixed: was incorrectly using 4
        } else {
          weekIndex = 0; // fallback
        }
        
        const weekIntervals = [1, 8, 15, 22, lastDayOfMonth];
        const intervalStart = weekIntervals[weekIndex];
        const intervalEnd = weekIndex < weekIntervals.length - 1 ? weekIntervals[weekIndex + 1] : lastDayOfMonth + 1;
        const dayOffset = (day - intervalStart) / (intervalEnd - intervalStart) * 0.8;
        
        position = weekIndex + dayOffset + (Math.random() - 0.5) * 0.1 + (index % 10) * 0.01;
      } else if (periodTab === 'year') {
        const monthPosition = itemDate.getMonth();
        const dayOfMonth = itemDate.getDate();
        const daysInMonth = new Date(itemDate.getFullYear(), itemDate.getMonth() + 1, 0).getDate();
        position = monthPosition + (dayOfMonth / daysInMonth) * 0.8 + (Math.random() - 0.5) * 0.1 + (index % 10) * 0.01;
      }
      
      return { ...item, position, uniqueId };
    }).filter(Boolean);

    return { transformedData: dataWithPositions, periods, labels, windowSize, randomTicks: randomTicks || [], randomLabels: randomLabels || [] };
  }, [data, periodTab, year, month, startdate, enddate]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const { transformedData, periods, labels, windowSize, randomTicks, randomLabels } = chartData;
  
  // Visible data calculation
  const visibleData = useMemo(() => {
    if (periodTab === 'custom' || periodTab === 'year') return transformedData;
    if (periodTab === 'week') return transformedData.filter(d => d.position >= currentIndex && d.position < currentIndex + 7);
    return transformedData.filter(d => d.position >= currentIndex && d.position < currentIndex + windowSize);
  }, [transformedData, currentIndex, periodTab, windowSize]);

  // Placeholder data
  const placeholderData = useMemo(() => {
    if (periodTab === 'custom') return randomTicks.map(tick => ({ position: tick, score: 0, type: 'placeholder', uniqueId: `placeholder-${tick}` }));
    if (periodTab === 'year') return Array.from({length: 12}, (_, i) => ({ position: i, score: 0, type: 'placeholder', uniqueId: `placeholder-${i}` }));
    
    const numTicks = periodTab === 'week' ? 7 : windowSize;
    return Array.from({length: numTicks}, (_, i) => {
      const position = currentIndex + i;
      return position < labels.length ? { position, score: 0, type: 'placeholder', uniqueId: `placeholder-${position}` } : null;
    }).filter(Boolean);
  }, [periodTab, randomTicks, currentIndex, windowSize, labels.length]);

  // Separate data by type
  const separatedData = useMemo(() => ({
    practiceData: visibleData.filter(d => d.type === 'Practice Assignment'),
    courseData: visibleData.filter(d => d.type === 'Course Assignment'),
    testPrepData: visibleData.filter(d => d.type === 'Test Prep'),
    testPrepPracticeData: visibleData.filter(d => d.type === 'Test Prep Practice')
  }), [visibleData]);

  // Navigation
  const goToPrevious = () => {
    if (periodTab === 'week' && currentIndex >= 7) {
      setCurrentIndex(currentIndex - 7);
    } else if (periodTab !== 'week' && currentIndex > 0) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };
  
  const goToNext = () => {
    if (periodTab === 'week' && currentIndex + 7 < labels.length) {
      setCurrentIndex(currentIndex + 7);
    } else if (periodTab !== 'week' && currentIndex + windowSize < labels.length) {
      setCurrentIndex(Math.min(labels.length - windowSize, currentIndex + 1));
    }
  };

  const canGoPrevious = periodTab === 'custom' || periodTab === 'year' ? false : (periodTab === 'week' ? currentIndex >= 7 : currentIndex > 0);
  const canGoNext = periodTab === 'custom' || periodTab === 'year' ? false : (periodTab === 'week' ? currentIndex + 7 < labels.length : currentIndex + windowSize < labels.length);

  // Event handlers - stable references
  const handleDotClick = useCallback((data) => {
    // Set the dot data with additional date information for proper title display
    const dotDataWithDate = {
      ...data.payload,
      // Add the actual date/period information for the title
      actualDate: data.payload.originalDate || data.payload.submission_date || data.payload.date,
      actualMonth: data.payload.month || data.payload.period,
      actualYear: data.payload.year || new Date().getFullYear(),
      // For monthly tab, ensure we have the selected month and year
      selectedMonth: periodTab === 'month' ? month : null,
      selectedYear: periodTab === 'month' ? year : null,
      // Add formatted month-year for monthly view
      monthYear: periodTab === 'month' ? `${getMonthName(month)}-${year}` : null
    };
    console.log('Setting dot data in EventOverViewGraph:', dotDataWithDate);
    setdot(dotDataWithDate);
  }, [setdot, periodTab, month, year]);

  // Optimized hover handlers with minimal state updates
  const handleDotMouseEnter = useCallback((data) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    if (data?.payload?.uniqueId && data.payload.uniqueId !== hoveredUniqueId) {
      setHoveredUniqueId(data.payload.uniqueId);
    }
  }, [hoveredUniqueId]);

  const handleDotMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredUniqueId(null);
      hoverTimeoutRef.current = null;
    }, 50);
  }, []);

  // Custom tick components that are stable across renders
  const CustomTick = useCallback((props) => {
    const { x, y, payload } = props;
    const value = payload.value;
    const labelText = labels[value] || '';
    const lines = labelText ? labelText.split('\n') : [''];
    
    return (
      <g transform={`translate(${x},${y})`}>
        <text 
          x={0} 
          y={0} 
          dy={16} 
          textAnchor="middle" 
          fill="#666" 
          fontSize="12"
        >
          <tspan x={0} dy="0">{lines[0]}</tspan>
          {lines[1] && <tspan x={0} dy="16">{lines[1]}</tspan>}
        </text>
      </g>
    );
  }, [labels]);
  
  const CustomTick_customcalender = useCallback((props) => {
    const { x, y, payload } = props;
    const value = payload.value;
    const labelText = randomLabels[randomTicks.indexOf(value)] || '';

    return (
      <g transform={`translate(${x},${y})`}>
        <text 
          x={0} 
          y={0} 
          dy={16} 
          textAnchor="middle" 
          fill="#666" 
          fontSize="12"
        >
          <tspan x={0} dy="0">{labelText}</tspan>
        </text>
      </g>
    );
  }, [randomLabels, randomTicks]);

    //bb  near the top of the component
const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);
useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth <= 767);
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);
  // Static axis configuration - never changes during hover
  const staticAxisProps = useMemo(() => {
    const baseAxis = {
      type: "number",
      dataKey: "position", 
      tickMargin: 12,
      axisLine: { stroke: '#e5e5e5' },
      tickLine: { stroke: '#e5e5e5' },
      interval: 0,
      allowDataOverflow: false
    };

    // if (periodTab === 'year') {
    //   return {
    //     ...baseAxis,
    //     domain: [-0.5, 11.5],
    //     ticks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    //     tick: { fontSize: 12, fill: '#666' },
    //     tickFormatter: (value) => labels[value] || ''
    //   };
    // }
    //bb new
    if (periodTab === 'year') {
  return {
    ...baseAxis,
    interval: isMobile ? 1 : 0,          // show Jan, Mar, May… on mobile
    domain: [-0.5, 11.5],
    ticks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    tick: { fontSize: isMobile ? 10 : 12, fill: '#666' },
    tickFormatter: (value) => labels[value] || ''
  };
}
     else if (periodTab === 'custom') {
      return {
        ...baseAxis,
        domain: [-0.5, periods.length - 0.5],
        ticks: randomTicks,
        tick: CustomTick_customcalender,
        tickFormatter: (value) => {
          const labelIndex = randomTicks.indexOf(value);
          return labelIndex !== -1 ? randomLabels[labelIndex] : '';
        }
      };
    } else if (periodTab === 'week') {
      const weekTicks = Array.from({length: 7}, (_, i) => currentIndex + i).filter(i => i < labels.length);
      return {
        ...baseAxis,
        domain: [currentIndex - 0.5, currentIndex + 6.5],
        ticks: weekTicks,
        tick: CustomTick,
        tickFormatter: (value) => labels[value] || ''
      };
    } else {
      const monthTicks = Array.from({length: windowSize}, (_, i) => currentIndex + i).filter(i => i < labels.length);
      return {
        ...baseAxis,
        domain: [currentIndex - 0.5, currentIndex + windowSize - 0.5],
        ticks: monthTicks,
        tick: { fontSize: 12, fill: '#666' },
        tickFormatter: (value) => labels[value] || ''
      };
    }
  }, [periodTab, periods.length, randomTicks, randomLabels, currentIndex, labels, windowSize, CustomTick, CustomTick_customcalender ,isMobile]);

  const getNavigationLabel = () => {
    if (periodTab === 'week') return `Week ${Math.floor(currentIndex / 7) + 1}`;
    if (periodTab === 'custom') return 'Custom Date Range';
    if (periodTab === 'month') return monthYear || 'Month View';
    if (periodTab === 'year') return "Months";
    return 'Period View';
  };
  useEffect(() => {
    setCurrentIndex(0);
  }, [periodTab, monthYear]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className='event-overview-Ggraph-section' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Ring size={40} lineWeight={5} speed={2} color="blue" />
      </div>
    );
  }

  return (
    <div 
      className='event-overview-Ggraph-section'
      style={{ 
        opacity: isAIGenerating ? 0.5 : 1, 
        pointerEvents: isAIGenerating ? 'none' : 'auto' 
      }}
    >
      <div className='overall-performance-graph-legend'></div>
 
      <div className='event-overview-Ggraph'>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: periodTab === 'week' ? 60 : 40,
            }}
          >
            <CartesianGrid 
              strokeDasharray="none" 
              stroke="#e5e5e5" 
              horizontal={true}
              vertical={false}
            />
            
            <XAxis {...staticAxisProps} />
            
            <YAxis 
              type="number"
              dataKey="score"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickCount={5}
              tickFormatter={(value) => `${value}%`}
              axisLine={{ stroke: '#e5e5e5' }}
              tickLine={{ stroke: '#e5e5e5' }}
              tick={{ fontSize: 14, fill: '#666' }}
            />
            
            <Tooltip 
              content={<CustomTooltip hoveredUniqueId={hoveredUniqueId} />} 
              cursor={false}
              allowEscapeViewBox={{ x: false, y: false }}
              wrapperStyle={{ pointerEvents: 'none' }}
            />
            
            {/* Placeholder scatter - invisible positioning */}
            <Scatter
              name="placeholder"
              data={placeholderData}
              fill="rgba(0,0,0,0)"
              fillOpacity={0}
              stroke="rgba(0,0,0,0)"
              strokeOpacity={0}
              strokeWidth={0}
              r={0}
            />
            
            {/* Data scatter components - only these re-render when data changes */}
            <Scatter
              className='practice-assignment'
              name="Practice Assignment"
              data={separatedData.practiceData}
              fill="#FF8C42"
              fillOpacity={0.8}
              stroke="#FF8C42"
              strokeWidth={1}
              onClick={handleDotClick}
              onMouseEnter={handleDotMouseEnter}
              onMouseLeave={handleDotMouseLeave}
              style={{ cursor: 'pointer' }}
            />
            
            <Scatter
            className='course-assignment'
              name="Course Assignment"
              data={separatedData.courseData}
              fill="#4A90E2"
              fillOpacity={0.8}
              stroke="#4A90E2"
              strokeWidth={1}
              onClick={handleDotClick}
              onMouseEnter={handleDotMouseEnter}
              onMouseLeave={handleDotMouseLeave}
              style={{ cursor: 'pointer' }}
            />
            
            <Scatter 
            className='test-prep'
              name="Test Prep"
              data={separatedData.testPrepData}
              fill="#9B59B6"
              fillOpacity={0.8}
              stroke="#9B59B6"
              strokeWidth={1}
              onClick={handleDotClick}
              onMouseEnter={handleDotMouseEnter}
              onMouseLeave={handleDotMouseLeave}
              style={{ cursor: 'pointer' }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      
      <div className='event-overview-Ggraph-navigation'>
        <button 
          onClick={goToPrevious}
          disabled={!canGoPrevious}
          style={{
            background: 'none',
            border: 'none',
            cursor: canGoPrevious ? 'pointer' : 'not-allowed',
            fontSize: '24px',
            color: canGoPrevious ? '#333' : '#ccc',
            padding: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (canGoPrevious) {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.style.color = '#4A90E2';
            }
          }}
          onMouseLeave={(e) => {
            if (canGoPrevious) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#333';
            }
          }}
        >
          &#8249;
        </button>
        
        <div style={{
          fontSize: '12px',
          color: '#000000',
          fontWeight: 'bold',
          alignSelf: 'center',
          userSelect: 'none',
        }}>
          {getNavigationLabel()}
        </div>
        
        <button 
          onClick={goToNext}
          disabled={!canGoNext}
          style={{
            background: 'none',
            border: 'none',
            cursor: canGoNext ? 'pointer' : 'not-allowed',
            fontSize: '24px',
            color: canGoNext ? '#333' : '#ccc',
            padding: '8px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (canGoNext) {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.style.color = '#4A90E2';
            }
          }}
          onMouseLeave={(e) => {
            if (canGoNext) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#333';
            }
          }}
        >
          &#8250;
        </button>
      </div>
    </div>
  );
};

export default EventOverViewGraph;